import axios from 'axios';
import { songContentEndpoint } from '../_constants/api.constant';
import { getSelfToken } from '../../_helpers/auth/secureToken';

// Lyrics variants, per-song SEO overrides, and the one read that returns them.
//
// The song LIST endpoints serialize none of this — `about`, `release_date`,
// `record_label`, `is_explicit`, `lyrics` and `seo` exist only on the detail
// serializer — so a card showing a row from the list has no idea whether the
// song has lyrics at all. Everything here is fetched when the creator opens the
// meta panel and not before.

function authHeader() {
    return { headers: { Authorization: 'Token ' + getSelfToken() } };
}

// Every call resolves with the response, error responses included. The backend
// answers an ownership failure with 403 and a `message`, and a validation
// failure with 400 and per-field errors — both are things the panel renders,
// not things it should treat as a thrown exception.
function settle(promise) {
    return promise
        .then(function (response) { return response; })
        .catch(function (error) { return error.response; });
}

/**
 * One song, through the DETAIL serializer.
 *
 * Same URL the song update posts to; `SongViewSet.get_serializer_class` swaps to
 * `SongDetailPageSerializer` for retrieve, which is what makes this the only
 * read that carries the new columns plus `lyrics`, `song_credit`, `seo` and
 * `structured_data`.
 */
export async function getSongDetail(songId) {
    return settle(axios.get(
        `${songContentEndpoint.SONG_DETAIL}?id=${songId}`,
        authHeader()
    ));
}

// ----------------------------- lyrics variants -----------------------------

// The scripts the backend accepts. Sent verbatim — these are the stored values,
// not labels, so the display name is derived rather than mapped, and a script
// added server-side needs a line here and nothing else.
export const LYRICS_SCRIPTS = [
    'latin', 'devanagari', 'arabic', 'bengali', 'gurmukhi', 'tamil', 'telugu', 'other',
];

export const scriptLabel = (script) => {
    const text = String(script || '');
    return text.charAt(0).toUpperCase() + text.slice(1);
};

// Every variant of one song. The list endpoint wraps rows in `{ data: [...] }`.
export async function listSongLyrics(songId) {
    return settle(axios.get(
        `${songContentEndpoint.LYRICS_LIST}?song_id=${songId}`,
        authHeader()
    ));
}

// `song` must be in the body — the backend reads it from the payload, not the
// URL, and answers 400 without it.
export async function createSongLyrics(payload) {
    return settle(axios.post(songContentEndpoint.LYRICS, payload, authHeader()));
}

export async function updateSongLyrics(lyricsId, payload) {
    return settle(axios.put(
        `${songContentEndpoint.LYRICS}?id=${lyricsId}`,
        payload,
        authHeader()
    ));
}

// Deleting the default variant is safe: the backend promotes the next one, so
// a song with lyrics always has one to show first.
export async function deleteSongLyrics(lyricsId) {
    return settle(axios.delete(
        `${songContentEndpoint.LYRICS}?id=${lyricsId}`,
        authHeader()
    ));
}

// --------------------------------- SEO meta ---------------------------------

export const TWITTER_CARDS = ['summary_large_image', 'summary'];

// The lengths the backend validates against. Checked in the form too, so an
// over-long title is caught while it is being typed rather than on save.
export const SEO_LIMITS = {
    meta_title: 60,
    meta_description: 160,
};

// The stored overrides for one song, or `{ data: [] }` when there are none.
//
// An absent row is NOT an error: it means the song page falls back to values
// derived from the song itself, which is the normal state for most songs.
export async function getSongSeo(songId) {
    return settle(axios.get(
        `${songContentEndpoint.SEO_META}?type=song&object_id=${songId}`,
        authHeader()
    ));
}

// POST and PUT both upsert the single row for this song, so one function covers
// "first save" and "edit" without the caller having to know which it is.
export async function saveSongSeo(songId, payload) {
    return settle(axios.post(
        `${songContentEndpoint.SEO_META}?type=song&object_id=${songId}`,
        payload,
        authHeader()
    ));
}

// Drops the overrides entirely, returning the song page to derived values.
// Different from saving blanks, which stores empty strings.
export async function deleteSongSeo(songId) {
    return settle(axios.delete(
        `${songContentEndpoint.SEO_META}?type=song&object_id=${songId}`,
        authHeader()
    ));
}
